import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft } from "lucide-react";
import { Shimmer } from "../components/Shimmer/Shimmer";
import {
  fetchPolicyCheckCompanyById,
  fetchPolicyCheckResult,
  isPolicyCheckPendingStatus,
  mapPolicyReportToTaskItems,
  type PolicyCheckCompany,
  type PolicyCheckReport,
} from "../api/policyCheck";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";
import { isAdminTabId } from "../components/AdminWorkspace/adminTabs";
import { formatDate, getStatusLabel } from "./RequestDetail/requestDetailUtils";
import type { PolicyCheckLocationState } from "./policyCheckLocationState";
import "./RequestDetail.scss";

const POLL_INTERVAL_MS = 5000;

const isRetryableResultError = (err: unknown): boolean => {
  if (!axios.isAxiosError(err)) return false;
  const status = err.response?.status;
  return status === 400 || status === 404;
};

export const PolicyCheckResult = () => {
  const { fileId } = useParams<{ fileId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const locationState = (location.state as PolicyCheckLocationState | null) ?? null;

  const [report, setReport] = useState<PolicyCheckReport | null>(null);
  const [company, setCompany] = useState<PolicyCheckCompany | null>(() =>
    locationState?.companyName || locationState?.inn
      ? {
          companyName: locationState.companyName,
          inn: locationState.inn,
        }
      : null
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const isPending = !error && (loading || !report || isPolicyCheckPendingStatus(report.status));

  const handleBack = useCallback(() => {
    if (report?.requestId) {
      navigate(`/policy-check/${report.requestId}`, {
        state: {
          companyName: company?.companyName,
          inn: company?.inn,
          adminTab: locationState?.adminTab,
        } satisfies PolicyCheckLocationState,
      });
      return;
    }

    const adminTab = locationState?.adminTab;
    if (isAdminTabId(adminTab)) {
      navigate({ pathname: "/", search: `?tab=${adminTab}` });
      return;
    }
    navigate({ pathname: "/", search: "?tab=policy-checks" });
  }, [company?.companyName, company?.inn, locationState?.adminTab, navigate, report?.requestId]);

  useEffect(() => {
    if (!fileId) return;

    let cancelled = false;
    setLoading(true);
    setError(null);

    fetchPolicyCheckResult(fileId)
      .then((data) => {
        if (!cancelled) {
          setReport(data);
          setError(null);
        }
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (isRetryableResultError(err)) {
          setReport((current) => current ?? { status: "in_progress", tasks: [] });
          setError(null);
          return;
        }
        setError(getApiErrorMessage(err, "Не удалось загрузить результат проверки"));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [fileId]);

  useEffect(() => {
    const requestId = report?.requestId?.trim();
    if (!requestId) return;
    if (company?.companyName && company?.inn) return;

    let cancelled = false;
    fetchPolicyCheckCompanyById(requestId)
      .then((item) => {
        if (!cancelled && item) setCompany(item);
      })
      .catch(() => {});

    return () => {
      cancelled = true;
    };
  }, [company?.companyName, company?.inn, report?.requestId]);

  useEffect(() => {
    if (!fileId || !isPending || error) return;

    const timer = window.setInterval(() => {
      fetchPolicyCheckResult(fileId)
        .then((data) => {
          setReport(data);
          setError(null);
        })
        .catch((err: unknown) => {
          if (isRetryableResultError(err)) return;
          setError(getApiErrorMessage(err, "Не удалось загрузить результат проверки"));
        });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(timer);
  }, [error, fileId, isPending]);

  if (!fileId) return null;

  const tasks = report ? mapPolicyReportToTaskItems(report) : [];
  const status = report?.status;

  return (
    <div className="detail-page">
      <div className="detail-container">
        <div className="detail-top-bar">
          <button type="button" className="detail-back" onClick={handleBack}>
            <ArrowLeft size={20} />
            <span>Назад</span>
          </button>
        </div>

        {loading && !report ? (
          <Shimmer rows={3} />
        ) : error ? (
          <div className="detail-card">
            <span className="detail-value">{error}</span>
          </div>
        ) : (
          <>
            <div className="detail-card">
              <div className="detail-row">
                <span className="detail-label">Компания</span>
                <span className="detail-value">{company?.companyName?.trim() || "—"}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">ИНН</span>
                <span className="detail-value">{company?.inn?.trim() || "—"}</span>
              </div>
            </div>

            <div className="detail-card">
              <div className="detail-dates-row">
                <div className="detail-row">
                  <span className="detail-label">Начало</span>
                  <span className="detail-value">
                    {report?.startDateTime ? formatDate(report.startDateTime) : "—"}
                  </span>
                </div>
                {report?.endDateTime ? (
                  <div className="detail-row">
                    <span className="detail-label">Окончание</span>
                    <span className="detail-value">{formatDate(report.endDateTime)}</span>
                  </div>
                ) : null}
              </div>
              <div className="detail-row">
                <span className="detail-label">Статус</span>
                <span className="detail-value">
                  {status ? getStatusLabel(status) : "—"}
                </span>
              </div>

              {isPending ? (
                <div className="detail-report-placeholder">
                  <div className="detail-report-progress">
                    <div className="detail-report-progress-bar" />
                  </div>
                  После выполнения проверки, здесь будут отображены результаты
                </div>
              ) : status === "error" ? (
                <div className="detail-report-placeholder detail-report-error">
                  При проверке произошла ошибка, повторите позднее
                </div>
              ) : tasks.length > 0 ? (
                <div className="detail-report-tasks detail-report-tasks--tall">
                  <table className="detail-tasks-table">
                    <thead>
                      <tr>
                        <th>Проверка</th>
                        <th>Результат</th>
                        <th>Детали</th>
                        <th>Рекомендация</th>
                        <th>Ответственность</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task, index) => (
                        <tr
                          key={task.taskId || `${task.checkName}-${task.result}-${index}`}
                          className={task.color ? `detail-task-row--${task.color}` : undefined}
                        >
                          <td>{task.checkNameDescription || task.checkName || "—"}</td>
                          <td>{task.resultDescription || task.result || "—"}</td>
                          <td>{task.details?.trim() || "—"}</td>
                          <td>{task.suggestion || "—"}</td>
                          <td>{task.responsibility || "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="detail-report-placeholder">Нет результатов</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
